import { z } from 'zod'

/**
 * `Via` records the surface used for an interactive or scheduled action.
 * It attributes execution, not authorship; scheduled digests act for recipients.
 *
 * This Zod schema is the single source of truth: the `Via` type is inferred
 * from it, and the JSONB envelope is validated against it on read/write.
 *
 * Field notes:
 * - `version` is stamped on every envelope so the stored JSONB shape can be
 *   migrated later.
 * - `type` is controlled by us — where the action was performed.
 * - `platform` only applies to first-party `slite` actions (so "is this
 *   first-party?" is a single `type === 'slite'` check). Forwarded to analytics
 *   as its own dimension.
 * - `auth` (on `mcp`) records the credential the MCP request used: an OAuth
 *   client (`oidc-client`) or a personal API key (`api-key`). Either way the
 *   transport is MCP, so the type stays `mcp`.
 * - `client` is freeform and informative, may be supplied by the client itself
 *   (e.g. "Claude Desktop"). Display-only.
 * - `id` is the stable client identifier (OIDC client row id, API key id,
 *   agent session id). Used by the GraphQL layer to resolve richer attribution
 *   metadata when available.
 * - `slack` is reserved for when a Slack surface starts authoring actions
 *   through the `User*` contexts — nothing does today, so it carries no fields.
 * - `digest` is a digest (super automation) run: scheduled on behalf of its
 *   recipient, or previewed interactively. `destination` is where the result
 *   is delivered — `web` is the interactive preview, rendered in the thread.
 * - `maintenance` is a KB maintenance run: its proposed writes surface as
 *   approval cards on the maintenance screen, so the surface is
 *   approval-capable even though the run itself is scheduled.
 */
export const ViaSchema = z.discriminatedUnion('type', [
  z.object({
    version: z.literal(1),
    type: z.literal('slite'),
    platform: z.enum(['app', 'mobile', 'desktop']),
  }),
  z.object({
    version: z.literal(1),
    type: z.literal('slite-agent'),
    id: z.string(),
  }),
  z.object({ version: z.literal(1), type: z.literal('slack') }),
  z.object({
    version: z.literal(1),
    type: z.literal('digest'),
    destination: z.enum(['email', 'slack', 'web']),
  }),
  z.object({ version: z.literal(1), type: z.literal('maintenance') }),
  z.object({
    version: z.literal(1),
    type: z.literal('mcp'),
    auth: z.enum(['oidc-client', 'api-key']),
    client: z.string(),
    id: z.string(),
  }),
  z.object({
    version: z.literal(1),
    type: z.literal('public-api'),
    client: z.string(),
    id: z.string(),
  }),
])

export type Via = z.infer<typeof ViaSchema>

export const VIA_VERSION = 1

/**
 * A single author's attribution: which user acted (`userId`) and how (`via`).
 * Kept as a wrapper rather than flattening `userId` into the envelope so `Via`
 * stays a pure, independently-validatable unit — `ViaSchema` is non-strict, so
 * a flattened `userId` would be silently stripped on parse.
 */
export const AuthorViaSchema = z.object({
  userId: z.string(),
  via: ViaSchema,
})

export type AuthorVia = z.infer<typeof AuthorViaSchema>

/**
 * Per-author attribution for a unit of work (a history window, a content
 * activity): every distinct surface each user acted through, so the consumer —
 * not the writer — decides what to display.
 */
export const ViasWithUserIdSchema = z.array(AuthorViaSchema)

export type ViasWithUserId = AuthorVia[]

/**
 * Stable identity of a `Via` for de-duplication. Two envelopes collide only
 * when they describe the *same* surface *and* client — so a user editing
 * through two different MCP clients (both `type: 'mcp'`) yields two entries,
 * while the same client appearing on many ops collapses to one.
 */
export function viaIdentityKey(via: Via): string {
  switch (via.type) {
    case 'slite':
      return `slite:${via.platform}`
    case 'slite-agent':
      return `slite-agent:${via.id}`
    case 'mcp':
      return `mcp:${via.id}`
    case 'public-api':
      return `public-api:${via.id}`
    case 'slack':
      return 'slack'
    case 'digest':
      return `digest:${via.destination}`
    case 'maintenance':
      return 'maintenance'
  }
}

function authorViaKey(userId: string, via: Via): string {
  return `${userId}\u0000${viaIdentityKey(via)}`
}

/**
 * Returns a new array with duplicate `(userId, via-identity)` pairs removed,
 * keeping the first occurrence of each. Pure — the input is not mutated.
 */
export function dedupeAuthorVias(list: AuthorVia[]): AuthorVia[] {
  const seen = new Set<string>()
  const out: AuthorVia[] = []
  for (const { userId, via } of list) {
    const key = authorViaKey(userId, via)
    if (!seen.has(key)) {
      seen.add(key)
      out.push({ userId, via })
    }
  }
  return out
}

/**
 * The distinct user ids whose edits came through MCP, derived from a
 * `viasWithUserId` list. Bridges the deprecated `mcpUserIds` surface to the new
 * attribution model so it can keep being emitted until consumers move to vias.
 */
export function mcpUserIdsFromVias(vias?: ViasWithUserId | null): string[] {
  if (!vias?.length) {
    return []
  }
  return [
    ...new Set(
      vias.filter((entry) => entry.via.type === 'mcp').map((e) => e.userId)
    ),
  ]
}

/**
 * Flattens a `Via` envelope into the discrete dimensions analytics backends
 * (Amplitude/Segment) can segment and chart on — they can't query nested
 * objects. Only the useful dimensions are kept: the surface (`viaType`), the
 * first-party platform (`viaPlatform`, slite only), and the client name
 * (`viaClient`). Internal/opaque fields (version, id, auth) are intentionally
 * omitted. For full attribution use the raw `Via` (e.g. the audit log column).
 */
export function flattenViaForAnalytics(via?: Via | null): {
  viaType?: string
  viaPlatform?: string
  viaClient?: string
} {
  if (!via) {
    return {}
  }

  return {
    viaType: via.type,
    viaPlatform: via.type === 'slite' ? via.platform : undefined,
    viaClient: 'client' in via ? via.client : undefined,
  }
}
